import { Controller } from '@hotwired/stimulus';
import { Modal } from 'bootstrap';

export default class extends Controller {

    static targets = [
        'modal',
        'modalTitle',
        'tag',
        'title',
        'description',
        'date',
        'daysBeforeCheckbox',
        'active',
        'repeatYearly',
        'sendEmail',
        'user',
        'userGroup',
        'destType',
        'userWrapper',
        'groupWrapper',
    ];

    static values = {
        url: String,
        deleteUrl: String,
        editUrl: String,
    };

    declare readonly urlValue: string;
    declare readonly deleteUrlValue: string;
    declare readonly editUrlValue: string;

    declare readonly modalTarget: HTMLElement;
    declare readonly modalTitleTarget: HTMLElement;
    declare readonly tagTarget: HTMLSelectElement;
    declare readonly titleTarget: HTMLInputElement;
    declare readonly descriptionTarget: HTMLTextAreaElement;
    declare readonly dateTarget: HTMLInputElement;
    declare readonly daysBeforeCheckboxTargets: HTMLInputElement[];
    declare readonly activeTarget: HTMLInputElement;
    declare readonly repeatYearlyTarget: HTMLInputElement;
    declare readonly sendEmailTarget: HTMLInputElement;
    declare readonly userTarget: HTMLSelectElement;
    declare readonly userGroupTarget: HTMLSelectElement;
    declare readonly destTypeTarget: HTMLSelectElement;
    declare readonly userWrapperTarget: HTMLElement;
    declare readonly groupWrapperTarget: HTMLElement;

    modal!: Modal;
    editingId: string | null = null;

    connect(): void {

        this.modal = new Modal(
            this.modalTarget
        );
    }

    openModal(): void {
        this.editingId = null;
        this.modalTitleTarget.textContent = 'Nuevo recordatorio';
        this.resetForm();
        this.modal.show();
    }

    openEditModal(event: Event): void {
        const button = event.currentTarget as HTMLElement;
        const card = button.closest('.reminder-card') as HTMLElement;

        if (!card) {
            return;
        }

        this.editingId = card.dataset.reminderId ?? null;
        this.modalTitleTarget.textContent = 'Editar recordatorio';

        const tagId = card.dataset.reminderTagId ?? '';
        const title = card.dataset.reminderTitle ?? '';
        const description = card.dataset.reminderDescription ?? '';
        const date = card.dataset.reminderDate ?? '';
        const daysBeforeRaw = card.dataset.reminderDaysBefore || '[]';
        const daysBefore: number[] = JSON.parse(daysBeforeRaw);
        const active = card.dataset.reminderActive ?? '1';
        const repeatYearly = card.dataset.reminderRepeatYearly ?? '0';
        const sendEmail = card.dataset.reminderSendEmail ?? '1';
        const userId = card.dataset.reminderUserId ?? '';
        const userGroupId = card.dataset.reminderUserGroupId ?? '';

        this.setSelectValue(this.tagTarget, tagId);
        this.titleTarget.value = title;
        this.descriptionTarget.value = description;
        this.setDate(date);
        this.daysBeforeCheckboxTargets.forEach(cb => {
            cb.checked = daysBefore.includes(parseInt(cb.value));
        });
        this.activeTarget.checked = active === '1';
        this.repeatYearlyTarget.checked = repeatYearly === '1';
        this.sendEmailTarget.checked = sendEmail === '1';
        this.setSelectValue(this.userTarget, userId);
        this.setSelectValue(this.userGroupTarget, userGroupId);

        this.destTypeTarget.value = userGroupId ? 'group' : 'user';
        this.toggleDest();

        this.modal.show();
    }

    toggleDest(): void {
        const isGroup = this.destTypeTarget.value === 'group';
        this.userWrapperTarget.classList.toggle('d-none', isGroup);
        this.groupWrapperTarget.classList.toggle('d-none', !isGroup);
    }

    closeModal(): void {

        this.modal.hide();
    }

    async save(): Promise<void> {
        const isGroup = this.destTypeTarget.value === 'group';

        const payload = {
            tag: this.getSelectValue(this.tagTarget),
            title: this.titleTarget.value,
            description: this.descriptionTarget.value,
            reminderDate: this.dateTarget.value,
            daysBefore: this.daysBeforeCheckboxTargets
                .filter(cb => cb.checked)
                .map(cb => parseInt(cb.value)),
            active: this.activeTarget.checked,
            repeatYearly: this.repeatYearlyTarget.checked,
            sendEmail: this.sendEmailTarget.checked,
            user: isGroup ? '' : this.getSelectValue(this.userTarget),
            userGroup: isGroup ? this.getSelectValue(this.userGroupTarget) : '',
        };

        if (this.editingId) {
            await this.update(payload);
        } else {
            await this.create(payload);
        }
    }

    async create(payload: object): Promise<void> {
        const response = await fetch(
            this.urlValue,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify(payload),
            }
        );

        const json = await response.json();

        if (!json.success) {
            return;
        }

        this.appendReminderCard(json.reminder);
        this.resetForm();
        this.closeModal();
    }

    async update(payload: object): Promise<void> {
        const url = this.editUrlValue.replace('/0', '/' + this.editingId);

        const response = await fetch(
            url,
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify(payload),
            }
        );

        const json = await response.json();

        if (!json.success) {
            return;
        }

        this.updateReminderCard(json.reminder);
        this.resetForm();
        this.closeModal();
    }

    async deleteReminder(
        event: Event
    ): Promise<void> {
        event.preventDefault();
        const button = event.currentTarget as HTMLElement;
        const id = button.dataset.id;
        if (!id) {
            return;
        }
        const response = await fetch(
            this.deleteUrlValue.replace(
                '/0',
                '/' + id
            ),
            {
                method: 'DELETE',

                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            }
        );

        const json = await response.json();
        if (!json.success) {
            return;
        }

        const card = button.closest(
            '.reminder-card'
        );

        if (card) {
            card.remove();
        }
    }

    setDate(date: string): void {
        const fp = (this.dateTarget as any)._flatpickr;
        if (fp) {
            fp.setDate(date, true);
        } else {
            this.dateTarget.value = date;
        }
    }

    setSelectValue(select: HTMLSelectElement, value: string): void {
        const ts = (select as any).tomselect;
        if (ts) {
            ts.setValue(value, true);
        } else {
            select.value = value;
        }
    }

    getSelectValue(select: HTMLSelectElement): string {
        const ts = (select as any).tomselect;
        return ts ? ts.getValue() : select.value;
    }

    resetForm(): void {
        this.setSelectValue(this.tagTarget, '');
        this.titleTarget.value = '';
        this.descriptionTarget.value = '';
        this.setDate('');
        this.daysBeforeCheckboxTargets.forEach(cb => { cb.checked = false; });
        this.activeTarget.checked = true;
        this.repeatYearlyTarget.checked = false;
        this.sendEmailTarget.checked = true;
        this.setSelectValue(this.userTarget, '');
        this.setSelectValue(this.userGroupTarget, '');
        this.destTypeTarget.value = 'user';
        this.toggleDest();
        this.editingId = null;
    }

    appendReminderCard(
        reminder: any
    ): void {
        const container = window.document.getElementById(
            'reminders-grid'
        );
        if (!container) {
            return;
        }

        container.insertAdjacentHTML(
            'beforeend',
            this.buildCardHtml(reminder)
        );
    }

    updateReminderCard(reminder: any): void {
        const card = this.element.querySelector(
            `.reminder-card[data-reminder-id="${reminder.id}"]`
        ) as HTMLElement;

        if (!card) {
            return;
        }

        card.dataset.reminderTagId = reminder.tagId ?? '';
        card.dataset.reminderTitle = reminder.title;
        card.dataset.reminderDescription = reminder.description ?? '';
        card.dataset.reminderDate = reminder.dateRaw;
        card.dataset.reminderDaysBefore = JSON.stringify(reminder.daysBefore ?? []);
        card.dataset.reminderActive = reminder.active ? '1' : '0';
        card.dataset.reminderRepeatYearly = reminder.repeatYearly ? '1' : '0';
        card.dataset.reminderSendEmail = reminder.sendEmail ? '1' : '0';
        card.dataset.reminderUserId = String(reminder.userId ?? '');
        card.dataset.reminderUserGroupId = String(reminder.userGroupId ?? '');

        const newCard = document.createElement('div');
        newCard.innerHTML = this.buildCardHtml(reminder);
        const inner = newCard.querySelector('.reminder-card') as HTMLElement;

        if (inner) {
            card.innerHTML = inner.innerHTML;
            Object.assign(card.dataset, inner.dataset);
        }
    }

    daysBeforeLabel(daysBefore: number[]): string {
        if (!daysBefore || daysBefore.length === 0) {
            return 'Todas las opciones';
        }

        return [...daysBefore].sort((a, b) => a - b).map(d => `${d} días`).join(', ');
    }

    buildCardHtml(reminder: any): string {
        const badge = reminder.tag
            ? `<span class="badge bg-${reminder.tagColor}">${reminder.tag}</span>`
            : '';

        const daysLabel = reminder.daysBeforeLabel ?? this.daysBeforeLabel(reminder.daysBefore);

        const statusBadges = `
            ${reminder.active
                ? '<span class="badge bg-success">Activo</span>'
                : '<span class="badge bg-secondary">Inactivo</span>'}
            ${reminder.repeatYearly
                ? '<span class="badge bg-info">Anual</span>'
                : ''}
            ${reminder.sendEmail
                ? '<span class="badge bg-primary">Email</span>'
                : ''}
        `;

        return `
            <div class="col-md-4 mb-3 reminder-card"
                 data-reminder-id="${reminder.id}"
                 data-reminder-tag-id="${reminder.tagId ?? ''}"
                 data-reminder-title="${reminder.title}"
                 data-reminder-description="${reminder.description ?? ''}"
                 data-reminder-date="${reminder.dateRaw}"
                 data-reminder-days-before="${JSON.stringify(reminder.daysBefore ?? [])}"
                 data-reminder-active="${reminder.active ? '1' : '0'}"
                 data-reminder-repeat-yearly="${reminder.repeatYearly ? '1' : '0'}"
                 data-reminder-send-email="${reminder.sendEmail ? '1' : '0'}"
                 data-reminder-user-id="${reminder.userId ?? ''}"
                 data-reminder-user-group-id="${reminder.userGroupId ?? ''}">

                <div class="card h-100">

                    <div class="card-body">

                        <div class="mb-2">
                            ${badge}
                        </div>

                        <h5 class="mb-2">
                            ${reminder.title}
                        </h5>

                        <div class="text-muted small mb-2">
                            ${reminder.date}
                        </div>

                        <div class="small mb-2">
                            Avisar ${daysLabel}
                        </div>

                        <div class="mb-3">
                            ${statusBadges}
                        </div>

                        <div class="small mb-3">
                            ${reminder.userGroup
                                ? `Grupo: ${reminder.userGroup}`
                                : `Usuario: ${reminder.user ?? '—'}`}
                        </div>

                        <div class="d-flex gap-2">

                            <button
                                type="button"
                                class="btn btn-outline-secondary btn-sm"
                                data-action="reminder#openEditModal"
                            >
                                Editar
                            </button>

                            <button
                                type="button"
                                class="btn btn-danger btn-sm"
                                data-id="${reminder.id}"
                                data-action="reminder#deleteReminder"
                            >
                                Eliminar
                            </button>

                        </div>

                    </div>

                </div>

            </div>
        `;
    }
}
